import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBranches,
  getBranch,
  createBranch,
  updateBranch,
  deleteBranch
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import { toast } from 'react-toastify';

// Hook to fetch all branches
export function useBranches() {
  const { organization, isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branches', organization?.id],
    queryFn: () => getBranches(organization.id),
    enabled: isInitialized && !!organization?.id
  });

  return {
    branches: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to fetch single branch
export function useBranch(branchId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['branch', branchId],
    queryFn: () => getBranch(branchId),
    enabled: !!branchId
  });

  return {
    branch: data?.data || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

// Hook to create branch
export function useCreateBranch() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createBranch,
    onSuccess: async () => {
      // Wait for query to be invalidated and refetched
      await queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch created successfully');
    },
    onError: error => {
      console.error('Create branch error:', error);
      toast.error('Failed to create branch');
    }
  });

  return {
    createBranch: mutation.mutate,
    createBranchAsync: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to update branch
export function useUpdateBranch() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, data }) => updateBranch(id, data),
    onSuccess: async () => {
      // Wait for both queries to be invalidated and refetched
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['branches'] }),
        queryClient.invalidateQueries({ queryKey: ['branch'] })
      ]);
      toast.success('Branch updated successfully');
    },
    onError: error => {
      console.error('Update branch error:', error);
      toast.error('Failed to update branch');
    }
  });

  return {
    updateBranch: mutation.mutate,
    updateBranchAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error
  };
}

// Hook to delete branch
export function useDeleteBranch() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: deleteBranch,
    onSuccess: async () => {
      // Wait for query to be invalidated and refetched
      await queryClient.invalidateQueries({ queryKey: ['branches'] });
      toast.success('Branch deleted successfully');
    },
    onError: error => {
      console.error('Delete branch error:', error);
      toast.error('Failed to delete branch');
    }
  });

  return {
    deleteBranch: mutation.mutate,
    deleteBranchAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    error: mutation.error
  };
}
